import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import moment from 'moment/moment';
import { objectToQueryString } from '@/utils';
import { MultiValue, SingleValue } from 'react-select';
import { SelectOptionType } from '@/components/element/Form/SelectOptionType';
import { SelectSingle } from '@/components/element/Form/SelectSingle';

type SearchCondition = {
  identifier: string;
  fiscal_year: number;
};

export const FinanceDataElement = () => {
  const { _w_ } = useCurrentWorkspace();
  const [searchCondition, setSearchCondition] = useState<SearchCondition>({
    identifier: _w_!.identifier,
    fiscal_year: 0,
  });
  const [queryString, setQueryString] = useState<string>('');

  useEffect(() => {
    setQueryString((_) => objectToQueryString({ ...searchCondition }));
  }, [searchCondition]);

  return (
    <div className="card mb-4">
      <div className="card-header d-flex align-items-center">
        <div>
          <h5 className="mb-0">管理会計データ</h5>
        </div>
        <button
          className="btn btn-icon-only btn-dark mx-2"
          data-bs-toggle="modal"
          data-bs-target="#modal-finance-data-filter"
        >
          <i className="fas fa-filter"></i>
        </button>
      </div>

      <div className="d-flex px-4 mb-0 align-items-center flex-wrap">
        {searchCondition.fiscal_year == 1 && <span className="badge rounded-pill badge-info mx-1 my-1">前年度</span>}
        {searchCondition.fiscal_year == 0 && <span className="badge rounded-pill badge-info mx-1 my-1">今年度</span>}
        {searchCondition.fiscal_year == 2 && <span className="badge rounded-pill badge-info mx-1 my-1">翌年度</span>}
      </div>

      <div className="card-body">
        <div className="table-responsive">
          <table className="table align-items-center">
            <thead>
              <tr>
                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2">データ</th>
                <th
                  className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2"
                  style={{ width: 140 }}
                ></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="text-xs px-2 font-weight-bold">管理会計売上データ</td>
                <td className="text-xs px-2 font-weight-bold">
                  <a
                    href={`${process.env.REACT_APP_API_URL}/api/workspaces/${
                      _w_!.identifier
                    }/dashboard/export_csv.csv?${queryString}`}
                    className="btn btn-icon btn-dark me-3"
                  >
                    <i className="fas fa-file-download" aria-hidden="true"></i> エクスポート
                  </a>
                </td>
              </tr>
              <tr>
                <td className="text-xs px-2 font-weight-bold">管理会計費用データ</td>
                <td className="text-xs px-2 font-weight-bold">
                  <a
                    href={`${process.env.REACT_APP_API_URL}/api/workspaces/${
                      _w_!.identifier
                    }/purchase_dashboard/export_csv.csv?${queryString}`}
                    className="btn btn-icon btn-dark me-3"
                  >
                    <i className="fas fa-file-download" aria-hidden="true"></i> エクスポート
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div className="card-footer pt-0 d-flex justify-content-end"></div>
      <FilterModal searchCondition={searchCondition} setSearchCondition={setSearchCondition} />
    </div>
  );
};

type FilterModalProps = {
  searchCondition: SearchCondition;
  setSearchCondition: Dispatch<SetStateAction<SearchCondition>>;
};
const FilterModal = ({ searchCondition, setSearchCondition }: FilterModalProps) => {
  const [condition, setCondition] = useState<SearchCondition>({ ...searchCondition });

  useEffect(() => {
    setCondition({ ...searchCondition });
  }, [searchCondition]);

  const onFiscalYearChange = (d: SingleValue<SelectOptionType>) => {
    setCondition((c) => ({ ...c, fiscal_year: d == null ? 0 : d.value }));
  };

  const onApplyClick = () => {
    setSearchCondition((_) => ({ ...condition }));
  };

  return (
    <div
      className="modal fade"
      id="modal-finance-data-filter"
      tabIndex={-1}
      role="dialog"
      aria-labelledby="modal-form"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-dialog-centered modal-md" role="document">
        <div className="modal-content">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="exampleModalLabel">
                フィルタ
              </h5>
              <button type="button" className="btn-close text-dark" data-bs-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="modal-body">
              <form>
                <div className="form-group">
                  <label>年度</label>
                  <FiscalYearSelect onChange={onFiscalYearChange} selectedId={condition.fiscal_year} />
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">
                閉じる
              </button>
              <button type="button" className="btn bg-gradient-primary" data-bs-dismiss="modal" onClick={onApplyClick}>
                適用
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
const FiscalYearSelect = ({
  onChange,
  selectedId,
}: {
  onChange: (d: SingleValue<SelectOptionType>) => void;
  selectedId: number;
}) => {
  const [options, setOptions] = useState<SelectOptionType[]>([
    { label: '前年度', value: 1 },
    { label: '今年度', value: 0 },
    { label: '翌年度', value: 2 },
  ]);

  return <SelectSingle options={options} selectedId={selectedId} onChange={onChange} />;
};
